import { Controller } from '@hotwired/stimulus'
import { ChatMessages, UserData, UserDataValue } from '../../api/models/types'
import { AnalyticsEventSpecial } from '../../api/models/analytics'
import { set, throttle } from 'lodash'
import computedValues from '../../helpers/computedValues'
import { generateElementSelectors } from '../../api/styles/selectors'
import jitsu from '../../helpers/jitsu'
import {
  connectUserData,
  doNotStoreComponentDataInCloud,
  getComponentContext,
  getFlow,
  lifecycleTrigger,
} from '../../helpers/controllerHelpers/utils'

export default class extends Controller {
  static targets = ['label', 'newMessage', 'messages']
  static values = {
    // userData: Object,
    showingOlderMessages: Boolean,
    loadingAiResponse: Boolean,
    lastUserMessageId: String,
  }

  component: ChatMessages
  disconnectUserData?: () => void
  declare userDataValue: UserDataValue
  declare newMessageTarget: HTMLFormElement
  declare messagesTarget: HTMLFormElement
  declare showingOlderMessagesValue: Boolean
  declare loadingAiResponseValue: Boolean
  declare lastUserMessageIdValue: String
  flowEl: Element | null
  pageId: string
  flowId: string

  connect() {
    const { component, flowEl, pageId } = getComponentContext(this)
    this.flowEl = flowEl
    this.flowId = flowEl?.getAttribute('data-flow-id-value') || ''
    this.pageId = pageId
    this.component = component as ChatMessages
    this.disconnectUserData = connectUserData(this, this.userDataValueChanged)
    lifecycleTrigger(this, 'mounted')
    this.hideQuickOptions(false) //this method will check on load and refresh, if the quick options should be hidden or not
  }

  disconnect() {
    lifecycleTrigger(this, 'unmounted')
    if (typeof this.disconnectUserData === 'function') this.disconnectUserData()
  }

  userDataValueChanged(newUserData: { [x: string]: string }) {
    let renderedShowButton = false
    const messagesHtml = ((newUserData[this.component.key] as unknown as any[]) || []).map(
      (message) => {
        //templating for hide_messages_before:
        const beforeTimestamp =
          typeof this.component.hide_messages_before === 'string'
            ? this.component.hide_messages_before
            : ''
        const beforeTimestampOk = computedValues(beforeTimestamp, this.userDataValue)
        const oldMessagesExist = message.timestamp < beforeTimestampOk ? true : false

        const pages = getFlow(this, true).pages || []
        const pagesById = pages.reduce((acc, p) => {
          acc[p.id] = p
          return acc
        }, {})
        const page = pagesById[this.pageId]

        //hiding old messages:
        //! Since chat messages is deprecated I'm harcoding the builder version to 3 (if needed will need to come from the json)
        const builderVersion = 3

        const chatMessageShowHideButtonClasses = generateElementSelectors({
          elementType: 'ChatMessageShowHideButtonClasses',
          component: this.component,
          page,
          elementTags: ['ChatMessageShowHideButtonClasses'],
          builderVersion,
        })
        if (
          this.component.hide_old_messages &&
          this.component.hide_messages_before &&
          !this.showingOlderMessagesValue
        ) {
          if (message.timestamp < beforeTimestampOk) {
            if (!renderedShowButton) {
              renderedShowButton = true
              return `<button class="${chatMessageShowHideButtonClasses.join(
                ' '
              )}" data-action="click->components--chat-messages#showHideOldMessages">Show old messages</button>`
            }
            return ''
          }
        }

        const chatMessageContainerClasses = generateElementSelectors({
          elementType: 'ChatMessageContainer',
          component: this.component,
          page,
          elementTags: ['ChatMessageContainer'],
          builderVersion,
        })
        const chatMessageContainerUserClasses = generateElementSelectors({
          elementType: 'ChatMessageContainerUser',
          component: this.component,
          page,
          elementTags: ['ChatMessageContainerUser'],
          builderVersion,
        })
        const chatMessageContainerAiClasses = generateElementSelectors({
          elementType: 'ChatMessageContainerAi',
          component: this.component,
          page,
          elementTags: ['ChatMessageContainerAi'],
          builderVersion,
        })
        const chatMessageTextClasses = generateElementSelectors({
          elementType: 'ChatMessageText',
          component: this.component,
          page,
          elementTags: ['ChatMessageText'],
          builderVersion,
        })
        const chatMessageAvatarClasses = generateElementSelectors({
          elementType: 'ChatMessageAvatar',
          component: this.component,
          page,
          elementTags: ['ChatMessageAvatar'],
          builderVersion,
        })
        const quickReplyOptionsContainer = generateElementSelectors({
          elementType: 'QuickReplyOptionsContainer',
          component: this.component,
          page,
          elementTags: ['QuickReplyOptionsContainer'],
          builderVersion,
        })
        const quickReplyOptionButton = generateElementSelectors({
          elementType: 'QuickReplyOptionButton',
          component: this.component,
          page,
          elementTags: ['QuickReplyOptionButton'],
          builderVersion,
        })
        const chatMessageSvgClasses = generateElementSelectors({
          elementType: 'ChatMessageSvgClasses',
          component: this.component,
          page,
          elementTags: ['ChatMessageSvgClasses'],
          builderVersion,
        })
        const chatMessageAiLeftContainerClasses = generateElementSelectors({
          elementType: 'ChatMessageAiLeftContainerClasses',
          component: this.component,
          page,
          elementTags: ['ChatMessageAiLeftContainerClasses'],
          builderVersion,
        })
        const chatMessageFeedbackButton = generateElementSelectors({
          elementType: 'ChatMessageFeedbackButton',
          component: this.component,
          page,
          elementTags: ['ChatMessageFeedbackButton'],
          builderVersion,
        })
        const chatMessageImageURLClasses = generateElementSelectors({
          elementType: 'ChatMessageImageURLClasses',
          component: this.component,
          page,
          elementTags: ['ChatMessageImageURLClasses'],
          builderVersion,
        })

        return `
        ${
          //showing 'Hide old messages' button if there are old messages
          !renderedShowButton && oldMessagesExist
            ? ((renderedShowButton = true),
              `<button class="${chatMessageShowHideButtonClasses.join(
                ' '
              )}" data-action="click->components--chat-messages#showHideOldMessages">Hide old messages</button>`)
            : ''
        }
        <div>
          ${
            this.component.show_sender && message.sender
              ? `<div class=""><img class="" src=${message.sender.avatar}>${message.sender.name}</div>` //not being used, need classes
              : ''
          }
          ${
            message.role != 'user'
              ? `<div class="${chatMessageContainerClasses.join(
                  ' '
                )}${chatMessageContainerAiClasses.join(' ')}"> 
                <div class="${chatMessageAiLeftContainerClasses.join(' ')}">
                  ${
                    this.component.bot_avatar_url
                      ? `<img class="${chatMessageAvatarClasses.join(' ')}" src="${
                          this.component.bot_avatar_url
                        }">`
                      : `<svg class="${chatMessageSvgClasses.join(
                          ' '
                        )}" width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="32" height="32" rx="2.50667" fill="white"/>
                      <path d="M23.8177 14.3433C24.0217 13.7289 24.0923 13.0779 24.0246 12.434C23.957 11.7901 23.7528 11.1681 23.4256 10.6094C22.9404 9.76496 22.1997 9.09641 21.3101 8.70012C20.4206 8.30382 19.4282 8.20026 18.4759 8.40437C17.935 7.8026 17.2452 7.35377 16.4759 7.10296C15.7065 6.85215 14.8848 6.80819 14.0931 6.9755C13.3014 7.14281 12.5677 7.51549 11.9656 8.05612C11.3635 8.59674 10.9143 9.28628 10.663 10.0555C10.0286 10.1856 9.42935 10.4496 8.90517 10.8298C8.381 11.2101 7.94402 11.6978 7.62344 12.2605C7.13305 13.1035 6.92346 14.0806 7.02498 15.0506C7.12649 16.0206 7.53384 16.9332 8.18814 17.6564C7.98335 18.2706 7.91208 18.9214 7.97908 19.5653C8.04609 20.2092 8.24982 20.8314 8.57667 21.3903C9.0624 22.235 9.80378 22.9037 10.694 23.3C11.5842 23.6963 12.5772 23.7997 13.5299 23.5954C13.9597 24.0794 14.4878 24.4661 15.0789 24.7298C15.6701 24.9934 16.3107 25.1279 16.9579 25.1243C17.9339 25.1252 18.885 24.8159 19.6738 24.2411C20.4625 23.6663 21.0483 22.8557 21.3464 21.9264C21.9807 21.7961 22.5799 21.532 23.1041 21.1517C23.6282 20.7715 24.0653 20.2838 24.386 19.7213C24.8706 18.8795 25.0766 17.906 24.9745 16.9401C24.8724 15.9742 24.4675 15.0652 23.8177 14.3433ZM16.9579 23.9299C16.1586 23.9312 15.3844 23.651 14.7709 23.1385L14.8788 23.0774L18.5119 20.9802C18.6023 20.9272 18.6774 20.8515 18.7298 20.7607C18.7821 20.6699 18.8099 20.567 18.8105 20.4622V15.3398L20.3464 16.2283C20.354 16.2322 20.3605 16.2377 20.3656 16.2446C20.3706 16.2515 20.3739 16.2594 20.3753 16.2678V20.5126C20.3733 21.4183 20.0127 22.2864 19.3722 22.9269C18.7318 23.5674 17.8637 23.928 16.9579 23.9299ZM9.61262 20.7932C9.21175 20.1009 9.06782 19.2895 9.20614 18.5017L9.31411 18.5665L12.9508 20.6637C13.0408 20.7165 13.1432 20.7443 13.2475 20.7443C13.3519 20.7443 13.4543 20.7165 13.5443 20.6637L17.9868 18.1024V19.8759C17.9863 19.8851 17.9838 19.894 17.9795 19.9021C17.9751 19.9102 17.969 19.9172 17.9615 19.9226L14.2817 22.045C13.4963 22.4974 12.5636 22.6197 11.6882 22.385C10.8128 22.1503 10.0663 21.5778 9.61262 20.7932ZM8.65582 12.8793C9.05946 12.1826 9.69658 11.6512 10.4544 11.3792V15.6959C10.453 15.8002 10.4797 15.9029 10.5315 15.9934C10.5834 16.0839 10.6586 16.1588 10.7493 16.2103L15.1702 18.7607L13.6342 19.6492C13.6259 19.6536 13.6167 19.6559 13.6072 19.6559C13.5978 19.6559 13.5886 19.6536 13.5803 19.6492L9.90763 17.5305C9.12377 17.0761 8.55192 16.3296 8.31729 15.4545C8.08267 14.5793 8.20439 13.6469 8.65582 12.8613V12.8793ZM21.2745 15.811L16.8393 13.2354L18.3716 12.3504C18.3799 12.346 18.3892 12.3436 18.3986 12.3436C18.408 12.3436 18.4173 12.346 18.4256 12.3504L22.0982 14.4727C22.6598 14.7968 23.1176 15.2738 23.4181 15.8483C23.7187 16.4227 23.8497 17.0708 23.7958 17.7169C23.7418 18.363 23.5052 18.9804 23.1136 19.4971C22.7219 20.0138 22.1914 20.4083 21.5839 20.6348V16.3181C21.5807 16.214 21.5505 16.1125 21.4962 16.0236C21.442 15.9347 21.3656 15.8615 21.2745 15.811ZM22.8033 13.5124L22.6953 13.4476L19.0659 11.3325C18.9754 11.2794 18.8723 11.2513 18.7673 11.2513C18.6623 11.2513 18.5593 11.2794 18.4687 11.3325L14.03 13.8937V12.1203C14.029 12.1113 14.0306 12.1022 14.0344 12.0939C14.0382 12.0857 14.0441 12.0787 14.0516 12.0735L17.7242 9.95473C18.2871 9.63045 18.9307 9.47315 19.5797 9.50122C20.2287 9.52929 20.8563 9.74158 21.389 10.1132C21.9218 10.4849 22.3378 11.0006 22.5882 11.6C22.8387 12.1994 22.9133 12.8577 22.8034 13.498L22.8033 13.5124ZM13.1917 16.6562L11.6559 15.7713C11.6482 15.7667 11.6416 15.7605 11.6366 15.753C11.6316 15.7456 11.6283 15.7371 11.627 15.7282V11.4944C11.6278 10.8449 11.8135 10.209 12.1624 9.66115C12.5113 9.11331 13.009 8.67611 13.5972 8.40067C14.1854 8.12524 14.8398 8.02295 15.484 8.10576C16.1282 8.18858 16.7355 8.45308 17.2349 8.86834L17.127 8.92955L13.494 11.0266C13.4035 11.0796 13.3285 11.1553 13.2761 11.2461C13.2238 11.3369 13.1959 11.4398 13.1954 11.5446L13.1917 16.6562ZM14.0263 14.8577L16.0046 13.7173L17.9868 14.8577V17.1383L16.0119 18.2786L14.0299 17.1383L14.0263 14.8577Z" fill="#18181B"/>
                    </svg>`
                  }
                  ${
                    this.component.allow_user_feedback &&
                    message.original_user_message_id &&
                    message.content !== 'Thank you for your feedback!'
                      ? `<button class="${chatMessageFeedbackButton.join(
                          ' '
                        )}" data-action="click->components--chat-messages#sendFeedback"><img id="flag_${
                          message.original_user_message_id
                        }" src="https://assets-modified.embeddables.com/hD1NU4pDpI0SpkDov3f2%2FFlag_2882153625995716.svg"></button>
                        <button class="${chatMessageFeedbackButton.join(
                          ' '
                        )}" data-action="click->components--chat-messages#sendFeedback"><img id="thumb_up---${
                          message.original_user_message_id
                        }" src="https://assets-modified.embeddables.com/hD1NU4pDpI0SpkDov3f2%2FThumb%20up_9243572440414107.svg"></button>
                        <button class="${chatMessageFeedbackButton.join(
                          ' '
                        )}" data-action="click->components--chat-messages#sendFeedback"><img id="thumb_down---${
                          message.original_user_message_id
                        }" src="https://assets-modified.embeddables.com/hD1NU4pDpI0SpkDov3f2%2FThumb%20down_6531372565077054.svg"></button>`
                      : ''
                  }
                </div>
                <div class="${chatMessageTextClasses.join(' ')} ai_text_container">${(
                  message.content.text || message.content
                ).replace(/\n/g, '<br>')}</div>
                </div>`
              : `<div class="${chatMessageContainerClasses.join(
                  ' '
                )} ${chatMessageContainerUserClasses.join(' ')}">
                <div class="${chatMessageAiLeftContainerClasses.join(' ')}">
                ${
                  this.component.user_avatar_url
                    ? `<img class="${chatMessageAvatarClasses.join(' ')}" src="${
                        this.component.user_avatar_url
                      }">`
                    : `<svg class="${chatMessageSvgClasses.join(
                        ' '
                      )}"width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="32" height="32" rx="2.5" fill="#09090B"/>
                    <path d="M24.898 23.1111C23.5812 20.8347 21.5521 19.2024 19.1841 18.4287C20.3555 17.7314 21.2655 16.6688 21.7745 15.4043C22.2835 14.1397 22.3634 12.743 22.0018 11.4287C21.6402 10.1143 20.8571 8.95503 19.7729 8.12881C18.6886 7.30258 17.3632 6.8551 16 6.8551C14.6368 6.8551 13.3114 7.30258 12.2271 8.12881C11.1429 8.95503 10.3598 10.1143 9.99824 11.4287C9.63664 12.743 9.71647 14.1397 10.2255 15.4043C10.7345 16.6688 11.6445 17.7314 12.8159 18.4287C10.4479 19.2016 8.41876 20.8339 7.10205 23.1111C7.05376 23.1898 7.02173 23.2774 7.00785 23.3688C6.99397 23.4601 6.99852 23.5532 7.02123 23.6428C7.04394 23.7323 7.08435 23.8164 7.14008 23.89C7.19581 23.9637 7.26572 24.0254 7.3457 24.0716C7.42568 24.1178 7.5141 24.1475 7.60574 24.159C7.69739 24.1705 7.7904 24.1635 7.8793 24.1384C7.96819 24.1133 8.05117 24.0707 8.12333 24.0131C8.19549 23.9554 8.25538 23.8839 8.29945 23.8028C9.92826 20.9878 12.8072 19.3071 16 19.3071C19.1928 19.3071 22.0717 20.9878 23.7006 23.8028C23.7446 23.8839 23.8045 23.9554 23.8767 24.0131C23.9488 24.0707 24.0318 24.1133 24.1207 24.1384C24.2096 24.1635 24.3026 24.1705 24.3943 24.159C24.4859 24.1475 24.5743 24.1178 24.6543 24.0716C24.7343 24.0254 24.8042 23.9637 24.8599 23.89C24.9156 23.8164 24.9561 23.7323 24.9788 23.6428C25.0015 23.5532 25.006 23.4601 24.9921 23.3688C24.9783 23.2774 24.9462 23.1898 24.898 23.1111ZM11.1585 13.0823C11.1585 12.1247 11.4425 11.1886 11.9745 10.3925C12.5064 9.59627 13.2626 8.97572 14.1472 8.60928C15.0319 8.24284 16.0054 8.14696 16.9445 8.33377C17.8837 8.52058 18.7464 8.98169 19.4234 9.65879C20.1005 10.3359 20.5616 11.1986 20.7485 12.1377C20.9353 13.0769 20.8394 14.0504 20.4729 14.935C20.1065 15.8197 19.486 16.5758 18.6898 17.1078C17.8936 17.6398 16.9576 17.9238 16 17.9238C14.7164 17.9224 13.4857 17.4119 12.5781 16.5042C11.6704 15.5965 11.1599 14.3659 11.1585 13.0823Z" fill="#FAFAFA"/>
                  </svg>`
                }
                </div>
                  <div class="${chatMessageTextClasses.join(' ')} user_text_container">${
                  message.content.text || message.content
                }</div>
                </div>                
                ${
                  message.image_url
                    ? `<img class="${chatMessageImageURLClasses.join(' ')}" src="${
                        message.image_url
                      }">`
                    : ''
                }`
          }
          ${
            message.quick_reply_options
              ? `<div class="${quickReplyOptionsContainer.join(' ')}">
              ${message.quick_reply_options
                .map((option) => {
                  return `<button class="${quickReplyOptionButton.join(
                    ' '
                  )}" data-action="click->components--chat-messages#actionButtonClicked">${
                    option.text
                  }</button>`
                })
                .join('')}
            </div>`
              : ''
          }
        </div>
      `
      }
    )

    let html = messagesHtml.join('')

    const messagesElement = this.element.querySelector('.ElementTag-MessagesContainer')
    if (messagesElement) {
      messagesElement.innerHTML = html
    }

    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight //scrolling to the bottom
  }

  generateId(prefix: string) {
    return prefix + '_' + Math.random().toString(20).slice(2) + Math.random().toString(20).slice(2)
  }

  async submitMessage(
    event?,
    functionCall?: {
      name: string
      arguments: any
    },
    isFunctionCall: boolean = false
  ) {
    // Should be using keydown.enter in the data-action properly, but this doesn't seem to work,
    // so we filter for 'Enter' in the submitMessage() controller function.
    if (event?.type === 'keydown' && event?.key !== 'Enter') return

    const userMessageId = this.generateId('message') as string
    const newMessage = this.newMessageTarget.value
    this.newMessageTarget.value = ''
    this.newMessageTarget.blur()

    if (newMessage) {
      this.addMessageToArray(newMessage, 'user', userMessageId)
      this.lastUserMessageIdValue = userMessageId
      if (!doNotStoreComponentDataInCloud(this.component)) {
        jitsu.track('message:sent', {
          flow_id: this.flowId,
          chat_messages_id: this.component.id,
          chat_messages_key: this.component.key,
          chat_messages_value: newMessage,
        })
      }
    }

    const openAiKey = this.component.open_ai_key as string
    const openAiKeySecured = this.component.open_ai_key_secure_name as string
    const overrideSystemMessage = this.component.override_system_message as boolean

    // Templating for prompt_template, system_message & open_ai_key_secure_name:
    const promptTemplateBeforeTemplating =
      typeof this.component.prompt_template === 'string' ? this.component.prompt_template : ''
    const promptTemplate = computedValues(promptTemplateBeforeTemplating, this.userDataValue)
    const systemMessageBeforeTemplating =
      typeof this.component.system_message === 'string' ? this.component.system_message : ''
    const systemMessage = computedValues(systemMessageBeforeTemplating, this.userDataValue)

    let myHeaders = new Headers()
    myHeaders.append('Content-Type', 'application/json')
    myHeaders.append(
      'Authorization',
      `Bearer ${openAiKeySecured ? `{{${openAiKeySecured}}}` : openAiKey}`
    )

    let previousMessages = //Stripping out the quick_reply_options and perform_action properties from the previous messages & any unnecessary tokens
      this.userDataValue[this.component.key]?.map((obj) => {
        if (!obj.content && obj.role === 'user') return
        const {
          quick_reply_options,
          perform_action,
          timestamp,
          original_user_message_id,
          prompt_template,
          image_url,
          id,
          ...otherProps
        } = obj
        return otherProps
      }) || []

    // Limiting the number of previous messages to 7(including system message)
    if (previousMessages.length > 7) {
      previousMessages = [previousMessages[0], ...previousMessages.slice(-6)]
    }

    const hardcodedSystemMessage = `Right now the date and time is ${new Date().toISOString()}. 
        Please ALWAYS respond using plain text and if you are giving options to the user please do it using the following format: 
        options header: '[QUICK REPLIES]:' line, followed by 3 options using letters (-a -b -c) not numbers.
        I might give you some more context here: ` as string

    let functionCallResults: any
    if (functionCall) {
      functionCallResults = await (window as any).FunctionCalls[functionCall.name](
        JSON.parse(functionCall.arguments)
      )
    }

    function checkForNonStringContent(previousMessages) {
      return previousMessages.map((message) => {
        //if the message is not a string, we need to return message.content.text
        if (typeof message.content !== 'string') {
          return {
            role: message.role,
            content: message.content.text,
          }
        } else {
          return message
        }
      })
    }

    //LUCIAN added this for the "ADD: To ChatMessages a "loading" icon/message" task
    let startTime
    const button = this.element.querySelector(
      'button.ElementType-ChatSubmitButton'
    ) as HTMLButtonElement
    const loading = this.element.querySelector('img.ElementType-ChatMessageLoading') as HTMLElement
    function setLoading(state) {
      if (!button || !loading) return
      if (state === true) {
        startTime = performance.now()
        button.style.display = 'none'
        loading.style.display = 'block'
      } else {
        console.log(`Ai response time: ${performance.now() - startTime} milliseconds.`)
        loading.style.display = 'none'
        button.style.display = 'block'
      }
    }
    //END of addition for task "ADD: To ChatMessages a "loaging" icon/message"

    let raw = {
      model: this.component.gpt_model || 'gpt-3.5-turbo',
      stream: this.component.stream,
      messages: [
        {
          role: 'system',
          content: overrideSystemMessage
            ? `${systemMessage}`
            : `${hardcodedSystemMessage} ${systemMessage}`,
        },
        ...(previousMessages.length > 0 ? checkForNonStringContent(previousMessages) : []),
        newMessage && {
          role: 'user',
          content: `${promptTemplate} ${newMessage}`, //this was included at the end of the message -> Please respond with string to which I can apply JSON.parse() and get a valid JSON object.
        },
        ...(functionCall
          ? [
              { role: 'assistant', function_call: functionCall, content: null },
              { role: 'function', name: functionCall.name, content: functionCallResults },
            ]
          : []),
      ],
      ...(this.component.functions ? { functions: this.component.functions } : {}), //Jeremy is this line ok?
    }

    //doublechecking if the "messages" array has any non-object elements
    if (raw.messages.some((message) => typeof message !== 'object')) {
      raw.messages = raw.messages.filter((message) => typeof message === 'object')
    }
    const finalRaw = JSON.stringify(raw)

    const requestOptions: RequestInit = {
      method: 'POST',
      headers: myHeaders,
      body: finalRaw,
      redirect: 'follow' as RequestRedirect,
    }

    this.loadingAiResponseValue = true //needs to be continued. At the moment is not doing anything
    const startTimeEpoch = Date.now()
    const URL = openAiKeySecured
      ? `https://proxy-secure.trysavvy.com/?url=${encodeURIComponent(
          `https://api.openai.com/v1/chat/completions`
        )}`
      : `https://api.openai.com/v1/chat/completions`

    //LUCIAN added next line for the "ADD: To ChatMessages a "loading" icon/message" task
    if (this.component.show_loading_while_ai_response) setLoading(true)
    fetch(URL, requestOptions)
      .then(async (response: Response) => {
        const endTimeEpoch = Date.now()
        if (this.component.stream /*&& !isFunctionCall*/) {
          const reader = response.body?.getReader()

          if (!reader) throw new Error('No reader')
          const decoder = new TextDecoder('utf-8')

          let stream = this.addMessageToArray(
            '',
            'assistant',
            this.generateId('message'),
            userMessageId,
            undefined,
            undefined,
            true
          )

          let functionCallName = ''
          let functionCallArgumentsStr = ''

          while (true) {
            const { done, value } = await reader.read()
            if (done) {
              break
            }
            // Massage and parse the chunk of data
            const chunk = decoder.decode(value)

            const lines = chunk.split('\n')
            const parsedLines = lines
              .map((line) => {
                return line.replace(/^data\: /, '').trim()
              }) // Remove the "data: " prefix
              .filter((line) => line !== '' && line !== '[DONE]') // Remove empty lines and "[DONE]"
              .map((line) => {
                return JSON.parse(line)
              }) // Parse the JSON string

            for (const parsedLine of parsedLines) {
              const { choices } = parsedLine
              const { delta } = choices[0]
              const { content, function_call } = delta
              // Update the UI with the new content
              if (content && stream) {
                stream(content)
              } else if (function_call) {
                // Add function_call word to overall function call string
                const { name, arguments: args } = function_call
                if (name) functionCallName = name
                if (arguments) functionCallArgumentsStr += args
                this.submitMessage(null, function_call, true)
                break
              }
            }
          }

          // Execute the function call
        } else {
          const result = await response.text()
          let newResult = JSON.parse(result)
          let content =
            newResult.choices[0].message.content?.text || newResult.choices[0].message.content
          let role = newResult.choices[0].message.role

          //We need to format the response to be a valid JSON object before we can parse it
          function removeTrailingCommas(jsonString) {
            const regex = /,\s*([\]}])/g
            let match
            while ((match = regex.exec(jsonString)) !== null) {
              const [, closingChar] = match
              const index = match.index
              if (closingChar === ']' || closingChar === '}') {
                jsonString = jsonString.slice(0, index) + jsonString.slice(index + 1)
              }
            }
            return jsonString
          }
          function addMissingCommas(jsonString) {
            let fixedJsonString = jsonString
            try {
              JSON.parse(jsonString)
            } catch (e) {
              const { message } = e
              const match = message.match(/position (\d+)/)
              if (match) {
                const pos = parseInt(match[1], 10)
                fixedJsonString = jsonString.slice(0, pos) + ',' + jsonString.slice(pos)
              }
            }
            return fixedJsonString
          }

          // If the new reply from the Ai is a function call, we need to execute it unless the previous message was also a function call and this can produce a loop (limited to 3 consecutive FCs)
          if (role === 'assistant' && newResult.choices[0].message.function_call) {
            if ((window as any).functionCallFlag === 3) {
              this.addMessageToArray(
                'Sorry, your question requires more analysis than what this free chat version provides.',
                'assistant',
                this.generateId('message'),
                this.lastUserMessageIdValue
              )
            } else {
              ;(window as any).functionCallFlag
                ? (window as any).functionCallFlag++
                : ((window as any).functionCallFlag = 1)
              this.submitMessage(null, newResult.choices[0].message.function_call, true)
            }
          } else {
            ;(window as any).functionCallFlag = 0
            if (content.includes('{')) {
              const startIndexResponse = content.indexOf('{')
              const endIndexResponse = content.lastIndexOf('}')
              let contentString = content.slice(startIndexResponse, endIndexResponse + 1)
              content = removeTrailingCommas(contentString)
              content = addMissingCommas(content)
              try {
                content = JSON.parse(content)
              } catch (error) {
                content = { text: 'There was an error. Please try refactoring your query.' }
              }
            } else {
              content = { text: content }
            }
            this.addMessageToArray(
              content.text,
              'assistant',
              this.generateId('message'),
              this.lastUserMessageIdValue,
              content.quick_actions ? content.quick_actions : undefined,
              content.perform_action ? content.perform_action : undefined
            )
          }

          const actionName = 'service:pinged' as any
          if (!doNotStoreComponentDataInCloud(this.component)) {
            jitsu.track(actionName, {
              flow_id: this.flowId,
            })
          }
        }
      })
      //LUCIAN added next "then"" for the "ADD: To ChatMessages a "loading" icon/message" task
      .then((result) => {
        if (this.component.show_loading_while_ai_response) {
          setLoading(false)
        }
      })
      .catch((error) => {
        console.log('error', error)
        if (this.component.show_loading_while_ai_response) {
          setLoading(false)
        }
      })

    this.hideQuickOptions(true)
  }

  hideQuickOptions(hide?: boolean) {
    const quickOptions = this.element.querySelector('.ElementTag-QuickInitialOptionsContainer')
    const quickOptionsContainer = quickOptions as HTMLElement

    if (
      quickOptionsContainer &&
      quickOptionsContainer.style.display !== 'none' &&
      (hide || this.userDataValue[this.component.key]?.length > 0)
    ) {
      quickOptionsContainer.style.display = 'none'
    }
  }

  actionButtonClicked(event: { target: any }) {
    const button = event.target
    const buttonText = button.innerText
    this.newMessageTarget.value = buttonText
    this.submitMessage()
  }

  sendFeedback(event: { target: any }, messageId: string) {
    const button = event.target.id
    const action = [{ type: 'feedback', value: button }] //Jeremy, I've added the [ ] here because VSC was complaining. Lucian.
    messageId = button.split('---')[1]
    this.addMessageToArray(
      'Thank you for your feedback!',
      'assistant',
      this.generateId('message'),
      messageId,
      undefined,
      action
    )
  }

  showHideOldMessages() {
    this.showingOlderMessagesValue = !this.showingOlderMessagesValue
    this.userDataValueChanged(this.userDataValue as any)
  }

  addMessageToArray(
    content: any,
    role: string,
    messageId: string,
    originalUserMessageId?: String,
    quickActions?: any[],
    performAction?: any[],
    streaming?: boolean
  ) {
    // if (role === 'stream') {

    // }
    this.loadingAiResponseValue = false

    if ((!content && !streaming) || (content === '\n' && role === 'user')) return

    const messages = this.userDataValue[this.component.key]
      ? this.userDataValue[this.component.key]
      : []

    const newMessagesArray = [
      ...messages,
      {
        //any new property added here should be removed from the data sent to Ai in the code --> "Stripping out the quick_reply_options and"
        id: messageId,
        ...(originalUserMessageId ? { original_user_message_id: originalUserMessageId } : {}),
        content: content,
        role: role,
        ...(quickActions ? { quick_reply_options: [...quickActions] } : {}),
        ...(performAction ? { perform_action: [...performAction] } : {}),
        timestamp: new Date().toISOString(),
        ...(role === 'user' && this.component.prompt_template
          ? { prompt_template: this.component.prompt_template }
          : {}),
      },
    ]

    //Adding user_request_id to the user messages
    // if (role === 'assistant' && this.userDataValue['supabase_user_request_id']) {
    //   let count = newMessagesArray.length - 1
    //   while (count >= 0) {
    //     if (newMessagesArray[count].role === 'user') {
    //       newMessagesArray[count].user_request_id = this.userDataValue['supabase_user_request_id']
    //       break
    //     }
    //     count--
    //   }
    // }

    // if (
    //   newMessagesArray[newMessagesArray.length - 1].role === 'assistant' &&
    //   this.userDataValue['supabase_user_request_id']
    // ) {
    //   newMessagesArray[newMessagesArray.length - 1].user_request_id =
    //     this.userDataValue['supabase_user_request_id']
    // }

    this.dispatch('update', {
      detail: {
        key: this.component.key,
        value: newMessagesArray,
      },
    })

    const self = this

    const index = newMessagesArray.length - 1

    const throttler = throttle(updateUserData, 500, { trailing: true, leading: false })

    // let controlRequestId = if )this.userDataValue['supabase_user_request_id']){
    //    this.userDataValue['supabase_user_request_id']
    // }

    function updateUserData(newMessagesArray) {
      self.dispatch('update', {
        detail: {
          key: self.component.key,
          value: newMessagesArray,
        },
      })
    }

    /* events: 
          message:sent
          service:pinged --> includes function calls this one is tricky because all this is happening inside addMessageToArray() and function calls are not executing this function
    */
    let actionName = '' as any
    let jitsuPaylod
    if (role === 'user') {
      // if (
      //   controlRequestId === this.userDataValue['supabase_user_request_id'] ||
      //   controlRequestId === null
      // ) {
      jitsuPaylod = {
        chat_message_payload: {
          sender: 'user',
          message_content: content,
          id: messageId,
        },
      } as AnalyticsEventSpecial
      actionName = 'message:sent'
      // }
    } else if (role === 'assistant') {
      // originalUserMessageId = this.userDataValue[this.component.key]?.slice(-1)[0]
      if (performAction && performAction[0].type === 'feedback') {
        jitsuPaylod = {}
        actionName = 'feedback:sent'
      } else {
        jitsuPaylod = {
          chat_message_payload: {
            id: messageId,
            sender: 'assistant',
            message_content: content,
            user_request_message_id: originalUserMessageId,
          },
        }
        actionName = 'message:sent'
      }
    }
    if (actionName && jitsuPaylod && !doNotStoreComponentDataInCloud(this.component)) {
      jitsu.track(actionName, { flow_id: this.flowId, ...jitsuPaylod })
    }

    function stream(newText: string) {
      newMessagesArray[index].content += newText
      throttler(newMessagesArray)
      ;(
        document.querySelector(
          `.ComponentKey-${self.component.key}.ElementType-ChatMessagesContainer > div:last-child .ElementType-ChatMessageText`
        ) as any
      ).innerText += newText
      ;(
        document.querySelector(
          `.ComponentKey-${self.component.key}.ElementType-ChatMessagesContainer`
        ) as any
      ).scrollTop = 99999999999999
    }

    return stream
  }
}
